/**
 * Helpers for the new My Products page.
 *
 * Product kinds are worked out on the front end only; nothing new is stored:
 *  - "sub"  : productType "subscription"
 *  - "free" : productType "classpass" with price €0 (free / migration credits, never sold)
 *  - "pass" : productType "classpass" with a price
 * The welcome "free trial credit" product is matched by its exact name in the backend,
 * so it is hidden here exactly like the old page did.
 */

export const FREE_TRIAL_NAME = "free trial credit";

export const productKind = (p) => {
  if (p?.productType === "subscription") return "sub";
  return parseFloat(p?.price) > 0 ? "pass" : "free";
};

export const KIND = {
  pass: { label: "Credit pass", band: "bg-indigo-bz", chip: "bg-[#F3E9FF] text-[#6A00BA]" },
  sub: { label: "Subscription", band: "bg-portland-orange", chip: "bg-[#FFE8EB] text-[#B4232F]" },
  free: { label: "Free credits", band: "bg-[#3B82F6]", chip: "bg-[#E7F0FF] text-[#1D4ED8]" },
};

export const toNumber = (v) => parseFloat(String(v ?? "").replace(",", ".")) || 0;

export const euro = (v) => {
  const n = toNumber(v);
  return "€" + (Number.isInteger(n) ? String(n) : n.toFixed(2).replace(".", ","));
};

const UNIT = { day: "day", week: "week", month: "month", year: "year" };
/** "month" or "3 months" for a billingPeriod {interval, interval_count}. */
export const periodText = (bp) => {
  const interval = bp?.interval;
  if (!interval) return "period";
  const count = parseInt(bp?.interval_count, 10) || 1;
  const unit = UNIT[interval] || interval;
  return count === 1 ? unit : `${count} ${unit}s`;
};

export const creditsOf = (p) => parseInt(p?.productFields?.noOfAllowedSessions, 10) || 0;

export const isUnlimitedSub = (p) => p?.productFields?.unlimitedCredits !== false;

export const validityText = (p) => {
  const f = p?.productFields || {};
  if (f.validUntilConsumed !== false) return "Valid until used";
  const d = parseInt(f.validityDays, 10) || 0;
  if (d === 365) return "Valid 1 year";
  return `Valid ${d} ${d === 1 ? "day" : "days"}`;
};

export const classesText = (p) => {
  const f = p?.productFields || {};
  if (f.allowAllClasses !== false) return "All activities";
  const names = (f.classList || []).map((c) => c.className).filter(Boolean);
  if (names.length) return names.join(", ");
  const n = (f.classId || []).length;
  return n ? `${n} selected ${n === 1 ? "activity" : "activities"}` : "No activities picked";
};

/** Card / list facts for one product. */
export const describe = (p) => {
  const kind = productKind(p);
  const credits = creditsOf(p);
  const price = toNumber(p?.price);
  if (kind === "sub") {
    const per = periodText(p?.productFields?.billingPeriod);
    const lim = !isUnlimitedSub(p);
    const renew = parseInt(p?.productFields?.creditsRenewalSessions, 10) || 0;
    return {
      kind,
      priceMain: euro(price),
      priceSub: `/ ${per}`,
      credits: lim && renew ? `${renew} credits every ${p?.productFields?.creditsRenewalCycle || per}` : "Unlimited credits",
      validity: `Renews every ${per}`,
      classes: classesText(p),
    };
  }
  if (kind === "free") {
    return {
      kind,
      priceMain: "€0",
      priceSub: "given by you",
      credits: credits ? `Up to ${credits} ${credits === 1 ? "credit" : "credits"} at a time` : "Credits chosen when you give them",
      validity: validityText(p),
      classes: classesText(p),
    };
  }
  return {
    kind,
    priceMain: euro(price),
    priceSub: credits > 1 ? `${euro(price / credits)} per credit` : "one credit",
    credits: `${credits} ${credits === 1 ? "credit" : "credits"}`,
    validity: validityText(p),
    classes: classesText(p),
  };
};

/** Sale status: free passes are "Not for sale" unless an old one was put on sale. */
export const saleStatus = (p) => {
  const onSale = p?.availableForSale !== false;
  if (productKind(p) === "free" && !onSale) return { key: "notforsale", label: "Not for sale", dot: "bg-[#3B82F6]", text: "text-[#1D4ED8]" };
  if (onSale) return { key: "onsale", label: "On sale", dot: "bg-[#22A55A]", text: "text-[#1F6B3A]" };
  return { key: "hidden", label: "Hidden", dot: "bg-[#B9B1BB]", text: "text-gray-500" };
};
